import { ActionManager, Camera, Engine, Scene, SceneOptions, TransformNode, Vector3 } from '@babylonjs/core';
import { ICamera } from 'Framework/Components/Controls/XView/Cameras';
import { IConfiguration } from 'Framework/Components/Controls/XView/Core';
import { IRenderer } from 'Framework/Components/Controls/XView/Renderers/IRenderer';

export abstract class BaseRenderer<TConfiguration extends IConfiguration> extends Scene implements IRenderer<TConfiguration> {
    protected constructor(engine: Engine, camera: ICamera, options?: SceneOptions) {
        super(engine, options);
        this.addTransformNode(new TransformNode('root'));

        this.metadata = { isRenderer: true };

        this.autoClear = false;
        this.actionManager = new ActionManager(this);

        this.camera = camera;
    }

    public load(configuration: TConfiguration): this {
        if (this._configuration) {
            this._clearScene();
        }

        this._configuration = configuration;

        this.camera.fov = configuration.Fov || Math.PI / 2;

        this.camera.maxFov = configuration.MaxFov || Math.PI / 2;
        this.camera.minFov = configuration.MinFov || Math.PI / 180;

        this.onLoad(configuration);

        return this;
    }

    public setOrigin(originX: number, originY: number, originZ: number): void {
        const transformNode = this.getTransformNodeByName('root');

        transformNode.position = new Vector3(originX, originY, originZ);
    }

    protected abstract onLoad(configuration: TConfiguration): void;

    public override dispose(): void {
        this._clearScene();

        super.dispose();
    }

    protected _clearScene(): void {
        while(this.textures.length > 0) {
            const texture = this.textures[0];
            texture.dispose();
        }

        while(this.materials.length > 0) {
            const material = this.materials[0];
            material.dispose();
        }

        while(this.meshes.length > 0) {
            const mesh = this.meshes[0];
            mesh.dispose(false, true);
        }
    }

    public get camera(): ICamera {
        return this._camera;
    }

    public set camera(value: ICamera) {
        this._camera = value;

        this.activeCamera = value as unknown as Camera;
    }

    protected _configuration: TConfiguration;
    private _camera: ICamera;
}